/**
 * Integrations — per-tenant connection manager for the 4 social publishers.
 *
 * For each platform the user can:
 *  - paste API credentials (encrypted at rest server-side)
 *  - see when the connection was last updated
 *  - test by submitting a published post (via "Publish now" inside Content Studio)
 *  - disconnect to wipe the encrypted blob
 *
 * Today only LinkedIn is wired live; the other 3 accept credentials and
 * mark the tenant as "ready" but the publish call returns "queued for next
 * session" until the live adapters land.
 */
import React, { useEffect, useState } from 'react';
import {
  Linkedin, Twitter, Instagram, Youtube, Sparkles, Check, AlertTriangle,
  Save, Trash2, KeyRound, Eye, EyeOff, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const PLATFORMS = [
  {
    id: 'linkedin', label: 'LinkedIn', Icon: Linkedin, accent: '#0A66C2',
    portal: 'https://www.linkedin.com/developers/apps',
    fields: [
      { key: 'client_id',       label: 'Client ID',       hint: 'From LinkedIn Developer Portal → Auth tab',   secret: false },
      { key: 'client_secret',   label: 'Client Secret',   hint: 'Reveal from Auth tab',                         secret: true  },
      { key: 'access_token',    label: 'Access Token',    hint: 'OAuth code → token (w_member_social + w_organization_social)', secret: true },
      { key: 'refresh_token',   label: 'Refresh Token',   hint: 'Returned alongside the access token',           secret: true  },
      { key: 'member_urn',      label: 'Member URN',      hint: 'e.g. urn:li:person:abc123 — from /v2/userinfo sub', secret: false },
      { key: 'organization_urn',label: 'Organization URN (optional)', hint: 'urn:li:organization:1234567 — for Company Page posts', secret: false },
    ],
  },
  {
    id: 'twitter', label: 'Twitter (X)', Icon: Twitter, accent: '#1DA1F2',
    portal: 'https://developer.twitter.com/en/portal/dashboard',
    fields: [
      { key: 'consumer_key',     label: 'Consumer Key',     hint: 'API Key from X dev portal', secret: true },
      { key: 'consumer_secret',  label: 'Consumer Secret',  hint: 'API Key Secret',            secret: true },
      { key: 'access_token',     label: 'Access Token',     hint: 'OAuth 1.0a user-context token',                 secret: true },
      { key: 'access_secret',    label: 'Access Token Secret', hint: 'Paired with the access token',              secret: true },
    ],
  },
  {
    id: 'instagram', label: 'Instagram', Icon: Instagram, accent: '#E4405F',
    portal: 'https://developers.facebook.com/apps',
    fields: [
      { key: 'page_access_token', label: 'Page Access Token', hint: 'Long-lived Page token from your linked Facebook Business Page', secret: true  },
      { key: 'ig_user_id',        label: 'IG Business Account ID', hint: 'From /me/accounts → instagram_business_account', secret: false },
    ],
  },
  {
    id: 'youtube', label: 'YouTube', Icon: Youtube, accent: '#FF0000',
    portal: 'https://console.cloud.google.com/apis/credentials',
    fields: [
      { key: 'client_id',     label: 'OAuth Client ID',     hint: 'From Google Cloud → APIs & Services → Credentials', secret: false },
      { key: 'client_secret', label: 'OAuth Client Secret', hint: 'Same screen as Client ID', secret: true  },
      { key: 'refresh_token', label: 'Refresh Token',       hint: 'From OAuth Playground or your own consent flow',    secret: true  },
      { key: 'channel_id',    label: 'Channel ID',          hint: 'youtube.com/channel/UCxxxx → the UC… part',         secret: false },
    ],
  },
];

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch effect. */
export default function Integrations() {
  const [statusMap, setStatusMap] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get('/integrations/status');
      setStatusMap(data);
    } catch (err) {
      // Don't toast — this fires on every mount and a 401/503 here just
      // means the user opened the page before auth was ready. But DO log
      // so the error is debuggable in the console when it actually breaks.
      console.warn('Integrations status fetch failed:', err?.response?.status, err?.message);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="ve-section" data-testid="integrations-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow flex items-center gap-2">
            <KeyRound size={14} className="text-[#8B3FC5]"/> Integrations
          </div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold">
            Bring your own keys. <span className="ve-gold-text">Auto-publish goes live.</span>
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-3xl">
            Paste your platform API credentials below. Everything is encrypted at rest with the platform&rsquo;s
            Fernet key — we never echo your secrets back to the UI. When credentials are in place,
            the <b>Publish now</b> button on approved Content Studio drafts pushes to the live platform.
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        {PLATFORMS.map((p) => (
          <PlatformCard key={p.id} platform={p}
                        status={statusMap[p.id] || { connected: false, live: p.id === 'linkedin' }}
                        onChanged={load}/>
        ))}
      </div>
    </div>
  );
}

function PlatformCard({ platform, status, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [showSecret, setShowSecret] = useState({});
  const [creds, setCreds] = useState({});
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const { Icon, accent } = platform;

  const save = async () => {
    const filled = Object.fromEntries(
      Object.entries(creds).filter(([_, v]) => v && v.trim()),
    );
    if (Object.keys(filled).length === 0) {
      toast.error('Paste at least one credential value.');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/integrations/${platform.id}`, {
        credentials: filled,
        label: label.trim() || null,
      });
      toast.success(`${platform.label} connected.`);
      setEditing(false);
      setCreds({});
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save.');
    } finally { setSaving(false); }
  };

  const disconnect = async () => {
    if (!confirm(`Disconnect ${platform.label}? Auto-publish will be disabled.`)) return;
    try {
      await api.delete(`/integrations/${platform.id}`);
      toast.success(`${platform.label} disconnected.`);
      onChanged && onChanged();
    } catch { toast.error('Could not disconnect.'); }
  };

  return (
    <div className="ve-card p-5" data-testid={`integration-card-${platform.id}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-md flex items-center justify-center"
               style={{ background: `${accent}10`, color: accent }}>
            <Icon size={22}/>
          </div>
          <div>
            <div className="font-bold text-base">{platform.label}</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mt-1 flex items-center gap-2">
              {status.connected ? (
                <span className="text-emerald-600 inline-flex items-center gap-1">
                  <Check size={11}/> Connected
                </span>
              ) : (
                <span className="text-slate-400">Not connected</span>
              )}
              {!status.live && (
                <span className="text-amber-600 inline-flex items-center gap-1" title="Adapter is queued for next iteration — credentials accepted but publishing requires the live adapter.">
                  <AlertTriangle size={11}/> Adapter pending
                </span>
              )}
            </div>
          </div>
        </div>
        <a href={platform.portal} target="_blank" rel="noopener noreferrer"
           className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 hover:text-[#5E1F8B] inline-flex items-center gap-1"
           data-testid={`integration-portal-${platform.id}`}>
          Dev portal <ExternalLink size={11}/>
        </a>
      </div>

      {!editing ? (
        <div className="flex items-center gap-2">
          <button onClick={() => setEditing(true)}
                  data-testid={`integration-edit-${platform.id}`}
                  className="ve-btn-primary text-xs">
            {status.connected ? 'Rotate keys' : 'Connect'}
          </button>
          {status.connected && (
            <button onClick={disconnect}
                    data-testid={`integration-disconnect-${platform.id}`}
                    className="text-[10px] uppercase tracking-[0.22em] font-bold text-rose-500 hover:text-rose-700 inline-flex items-center gap-1">
              <Trash2 size={11}/> Disconnect
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3 border-t border-slate-200 pt-4">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-1">Label (internal)</div>
            <input value={label} onChange={(e) => setLabel(e.target.value)}
                   placeholder={`${platform.label} account`}
                   data-testid={`integration-label-${platform.id}`}
                   className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:border-[#8B3FC5]"/>
          </label>
          {platform.fields.map((f) => (
            <label key={f.key} className="block">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">{f.label}</span>
                {f.secret && (
                  <button type="button"
                          onClick={() => setShowSecret((s) => ({ ...s, [f.key]: !s[f.key] }))}
                          className="text-[10px] text-slate-400 hover:text-slate-700 inline-flex items-center gap-1">
                    {showSecret[f.key] ? <><EyeOff size={10}/> Hide</> : <><Eye size={10}/> Show</>}
                  </button>
                )}
              </div>
              <input value={creds[f.key] || ''}
                     onChange={(e) => setCreds((c) => ({ ...c, [f.key]: e.target.value }))}
                     type={f.secret && !showSecret[f.key] ? 'password' : 'text'}
                     placeholder={f.hint}
                     data-testid={`integration-field-${platform.id}-${f.key}`}
                     className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:border-[#8B3FC5] font-mono"/>
              <div className="text-[10px] text-slate-400 mt-0.5">{f.hint}</div>
            </label>
          ))}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => { setEditing(false); setCreds({}); }}
                    className="ve-btn-secondary text-xs">Cancel</button>
            <button onClick={save} disabled={saving}
                    data-testid={`integration-save-${platform.id}`}
                    className="ve-btn-primary text-xs disabled:opacity-50">
              <Save size={12}/> {saving ? 'Saving…' : 'Save credentials'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
